/**
 * Money formatting + amount-input helpers, shared across the wallet and ad-budget UIs so
 * every figure reads the same way ($1,234) and every amount field behaves identically.
 *
 * The amount-input pattern: store a RAW numeric string (digits + an optional single decimal,
 * ≤ 2 places) so the value stays trivially parseable; show that raw value WHILE the field is
 * focused (so the caret behaves natively as the user types) and the GROUPED value when blurred
 * ("7,890"). We deliberately don't re-group under the caret on every keystroke — that needs
 * caret restoration, which is fiddly and jumps the cursor to the end.
 */

/** Coerce a possibly-NaN/undefined value to a finite number (so a bad figure renders as 0). */
export const finiteNumber = (n: unknown): number => {
  const v = Number(n ?? 0);
  return Number.isFinite(v) ? v : 0;
};

/** A whole/decimal figure as grouped USD ("1234.5" → "$1,234.5"). */
export const formatMoney = (n: number): string => `$${finiteNumber(n).toLocaleString('en-US')}`;

// --- Multi-currency display (symbol + trailing ISO code) --------------------
// Mirrors the configurator's price format: USD and MXN both use the `$` glyph (the operator
// site writes "$100 MXN", not "MX$100"), so the trailing ISO code is what disambiguates them.
// Used where the symbol is a separately-styled element and the value slot carries "amount +
// code", so "$" + "24.99 USD" reads "$24.99 USD".

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  MXN: '$',
  BRL: 'R$',
  CLP: '$',
  COP: '$',
  ARS: '$',
  PEN: 'S/',
  VES: 'Bs',
  ZAR: 'R',
  CAD: '$',
  EUR: '€',
  GBP: '£',
  RON: 'lei ',
};

/** The currency glyph for an ISO code ("USD" → "$"), falling back to the code + a space. */
export const currencySymbol = (code: string): string => CURRENCY_SYMBOLS[code] ?? `${code} `;

/** An amount with its ISO code but NO symbol ("24.99 USD"). Pairs with {@link currencySymbol}
 *  (rendered separately) to read "$24.99 USD".
 *  @param minFractionDigits Pad to this many decimals. Default 0, so "100 USD" stays "100 USD";
 *  pass 2 for prices, where a bare tenth ("12.5 USD") reads as a mistake. */
export const formatAmountWithCode = (amount: number, code: string, minFractionDigits = 0): string =>
  `${finiteNumber(amount).toLocaleString('en-US', {
    minimumFractionDigits: minFractionDigits,
    maximumFractionDigits: 2,
  })} ${code}`;

/** Compact number for tight spaces (the mobile header): 1100 → "1.1K", 47797.64 → "47.8K", 1.2M.
 *  Values under 1,000 render in full ("500"). */
export const formatCompact = (value: number): string =>
  new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(finiteNumber(value));

/** Negative-signed grouped USD ("-$1,234"). */
export const formatNegMoney = (n: number): string => `-$${finiteNumber(n).toLocaleString('en-US')}`;

/** Strip an input down to a raw amount: digits, a single dot, at most two decimals. */
export const cleanAmountInput = (value: string): string => {
  let s = value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
  const dot = s.indexOf('.');
  if (dot !== -1) s = `${s.slice(0, dot + 1)}${s.slice(dot + 1, dot + 3)}`; // cap at 2 decimals
  return s;
};

/** Group a raw amount string for display ("1234.5" → "1,234.5"), keeping a lone trailing dot
 *  and any partial decimals exactly as typed. Returns '' for an empty value (shows the
 *  placeholder). No leading "$" — the field renders its own currency prefix. */
export const formatAmountDisplay = (raw: string): string => {
  if (raw === '') return '';
  const [intPart, decPart = ''] = raw.split('.');
  const grouped = intPart ? Number(intPart).toLocaleString('en-US') : '0';
  return raw.includes('.') ? `${grouped}.${decPart}` : grouped;
};
